import type { ReactNode } from 'react';
import { PrimitiveButton } from '../PrimitiveButton/index.js';

export interface SwatchOption<TValue extends string> {
  value: TValue;
  render: ReactNode;
  label: string;
}

export interface SwatchPickerProps<TValue extends string> {
  ariaLabel: string;
  options: readonly SwatchOption<TValue>[];
  value: TValue | null;
  onChange: (next: TValue) => void;
  allowDeselect?: boolean;
  onClear?: () => void;
  className?: string;
  size?: number;
}

export function SwatchPicker<TValue extends string>(
  props: SwatchPickerProps<TValue>,
): ReactNode {
  const {
    ariaLabel,
    options,
    value,
    onChange,
    allowDeselect = false,
    onClear,
    className,
    size = 26,
  } = props;
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={['vq-swatch-picker', className].filter(Boolean).join(' ')}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        const handleClick = (): void => {
          if (selected && allowDeselect) {
            if (onClear) onClear();
            else onChange('' as TValue);
            return;
          }
          onChange(opt.value);
        };
        return (
          <PrimitiveButton
            key={opt.value}
            role="radio"
            aria-checked={selected}
            aria-label={opt.label}
            title={opt.label}
            onClick={handleClick}
            className="vq-swatch-picker__tile"
            data-selected={selected ? 'true' : 'false'}
            style={{ width: size, height: size }}
          >
            {opt.render}
          </PrimitiveButton>
        );
      })}
    </div>
  );
}
